"use client";

import { buildQueryString } from "@/lib/buildQueryString";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

let _activePartnerCode: string | null = null;

export function setActivePartnerCode(code: string | null): void {
  _activePartnerCode = code;
}

function getCsrfToken(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|; )cp_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
}

type RequestMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

const PRE_SESSION_AUTH_PATHS = [
  "/customer-portal/auth/login",
  "/customer-portal/auth/challenge",
];

function isPreSessionAuthPath(path: string): boolean {
  return PRE_SESSION_AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}?`));
}

async function request<T>(
  method: RequestMethod,
  path: string,
  body?: unknown,
  skipRefresh = false
): Promise<T> {
  const mutating = method !== "GET";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (mutating) {
    headers["X-CSRF-Token"] = getCsrfToken();
  }
  if (_activePartnerCode) {
    headers["x-active-partner"] = _activePartnerCode;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const effectiveSkipRefresh = skipRefresh || isPreSessionAuthPath(path);

  if (res.status === 401 && !effectiveSkipRefresh) {
    const refreshed = await fetch(`${API_BASE}/customer-portal/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (refreshed.ok) {
      return request<T>(method, path, body, true);
    }
    if (typeof window !== "undefined") {
      window.location.href = "/login?reason=session_expired";
    }
    throw new Error("session_expired");
  }

  if (res.status === 403 && !skipRefresh) {
    const payload = await res.clone().json().catch(() => ({}));
    if (payload?.code === "MFA_ENROLMENT_REQUIRED" && typeof window !== "undefined" && !window.location.pathname.startsWith("/mfa-setup")) {
      const returnTo = encodeURIComponent(window.location.pathname);
      window.location.href = `/mfa-setup?returnTo=${returnTo}`;
      throw new Error("mfa_enrolment_required");
    }
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    const err = new Error(payload?.message ?? `HTTP ${res.status}`);
    (err as any).status = res.status;
    (err as any).code = payload?.code;
    throw err;
  }

  return res.json() as Promise<T>;
}

export function buildApiUrl(
  path: string,
  params: Record<string, string | number | boolean | null | undefined> = {},
): string {
  const merged = { ...params, activePartner: _activePartnerCode ?? undefined };
  const qs = buildQueryString(merged);
  return qs ? `${API_BASE}${path}?${qs}` : `${API_BASE}${path}`;
}

export const apiClient = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
